// src/services/attendanceService.ts
import { api } from "@/services/api";
import {
  CreateShiftRequest,
  CreateHolidayRequest,
  CreateScheduleRequest,
  BulkAssignScheduleRequest,
  CreateWorkstationEvaluationRequest,
  CreateAbsenceRequest,
} from "@/types/attendance";

export const attendanceService = {
  // --- SHIFTS ---
  getShifts: async (slug: string) => {
    return api.get("/attendance/shifts", { params: { slug } });
  },

  createShift: async (data: CreateShiftRequest) => {
    return api.post("/attendance/shifts", data);
  },

  updateShift: async (id: string, data: Partial<CreateShiftRequest>) => {
    return api.put(`/attendance/shifts/${id}`, data);
  },

  deleteShift: async (id: string, slug: string) => {
    return api.delete(`/attendance/shifts/${id}`, { data: { slug } });
  },

  // --- HOLIDAYS ---
  getHolidays: async (slug: string) => {
    return api.get("/attendance/holidays", { params: { slug } });
  },

  createHoliday: async (data: CreateHolidayRequest) => {
    return api.post("/attendance/holidays", data);
  },

  deleteHoliday: async (id: string, slug: string) => {
    return api.delete(`/attendance/holidays/${id}`, { data: { slug } });
  },

  // --- SCHEDULES ---
  getSchedules: async (slug: string, start_date?: string, end_date?: string) => {
    return api.get("/attendance/schedules", { params: { slug, start_date, end_date } });
  },

  createSchedule: async (data: CreateScheduleRequest) => {
    return api.post("/attendance/schedules", data);
  },

  deleteSchedule: async (id: string, slug: string) => {
    return api.delete(`/attendance/schedules/${id}`, { params: { slug } });
  },

  bulkAssignSchedule: async (data: BulkAssignScheduleRequest) => {
    return api.post("/attendance/schedules/bulk", data);
  },

  // --- ABSENCES ---
  getAbsences: async (slug: string, employee_id?: string, start_date?: string, end_date?: string) => {
    return api.get("/attendance/absences", { params: { slug, employee_id, start_date, end_date } });
  },

  createAbsence: async (data: CreateAbsenceRequest) => {
    return api.post("/attendance/absences", data);
  },

  deleteAbsence: async (id: string, slug: string) => {
    return api.delete(`/attendance/absences/${id}`, { params: { slug } });
  },

  // --- PERFORMANCE & QUALITY ---
  getPerformanceRanking: async (
    slug: string,
    start_date?: string,
    end_date?: string,
  ) => {
    return api.get("/attendance/performance/ranking", {
      params: { slug, start_date, end_date },
    });
  },

  getWorkstationEvaluationHistory: async (
    slug: string,
    employee_id: string,
    start_date?: string,
    end_date?: string,
  ) => {
    return api.get("/attendance/workstation-evaluations", {
      params: { slug, employee_id, start_date, end_date },
    });
  },

  getIncidentPerformanceScore: async (
    slug: string,
    employee_id: string,
    start_date?: string,
    end_date?: string,
  ) => {
    return api.get("/attendance/performance/incident-score", {
      params: { slug, employee_id, start_date, end_date },
    });
  },

  createWorkstationEvaluation: async (data: CreateWorkstationEvaluationRequest) => {
    return api.post("/attendance/workstation-evaluations", data);
  },

  createQualityIncident: async (data: Record<string, unknown>) => {
    return api.post("/attendance/quality/incidents", data);
  },

  createProductionTask: async (data: Record<string, unknown>) => {
    return api.post("/attendance/production/tasks", data);
  },

  // --- ATTENDANCE ---
  getAttendance: async (slug: string, params?: { date?: string; employee_id?: string }) => {
    return api.get("/attendance", { params: { slug, ...params } });
  },

  uploadAttendanceExcel: async (data: { slug: string; file_base64: string }) => {
    return api.post("/attendance/upload", data);
  },
};
